
import React, { useEffect, useState } from 'react'
import { Row, Col, Alert, Form, FormCheck, Badge, CloseButton } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../../components/Card'

import { VmcStatusItem } from '../../../features/vmcs';
import { getVmcs, getPropertyInfo } from '../../../services/api/vmc';
import { ApiError, VmcStatusDto as VmcStatus } from '../../../services/openapi';

import { useKeycloak } from '@react-keycloak/web'

import SearchInput from '../../../components/SearchInput';
import ListPagination from '../../../components/ListPagination';
import RefreshPull from '../../../components/RefreshPull';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { peekPendingSelectedId, clearPendingSelectedId } from '../../../hooks/pendingSelection';
import { useTranslation } from 'react-i18next';
import { NavContext } from '../../../components/dto/NavContext';
import { resetNavContext } from '../../../hooks/navContext';

const Vmcs = () => {
    const [vmcs, setVmcs] = useState<VmcStatus[]>([]);        
    const [VmcCount, setVmcCount] = useState<string>("0");    
    const [pagingString, setPagingString] = useState<string>("0-25");    
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [tableBodyHeight, setTableBodyHeight] = useState<number>(380);
    const [formData, setFormData] = useLocalStorage<string>("vmcs", "formData", "");
    const [searchData, setSearchData] = useLocalStorage<Record<string, string>>("vmcs", "searchData", {});
    const [filterData, setFilterData] = useLocalStorage<string>("vmcs", "filterData", "");
    const [currentPage, setCurrentPage] = useLocalStorage<number>("vmcs", "currentPage", 0);
    const [siteFilter, setSiteFilter] = useLocalStorage<number[]>("sites", "siteFilter", []);
    const [siteFilterQuery, setSiteFilterQuery] = useLocalStorage<string>("sites", "siteFilterQuery", "");
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [newVmc, setNewVmc] = useState<VmcStatus>();
    const [selectedId, setSelectedId] = useState<number | null>(() => peekPendingSelectedId("vmcs"));
    const [rowClickCount, setRowClickCount] = useState({});

    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const dummyVmc: VmcStatus = {
        identifier: "",
        vmcStatus: "",
        vmcStatusEnum: 1,
        siteId: "",
        note: "",
        inactivityTimeoutInSeconds: 60,
        isActive: "True",
        id: 0,
        description: "",
        productIds: [],
        vmcTypeId: 0,
        type: "",
        sessionMode: "False",
        pollIntervalForLimit: "False",
        clearOutputsAfterActivatedInput: "False",
        inputDeterminesAvailability: "False",
        inputDeterminesDeliveryInProgress: "False",
        maxChargeSpeedInKwh: "",
        parallelDeliveries: "False",
        pulsePeriodInMs: "",
        pulseWidthInMs: "",
        sendPulsePeriodValue: "False",
        vmcResetInputName: "",
        waitCountInputInactive: 1,
        temperature: 0
    }

    const setData = (data: VmcStatus) => {
        localStorage.setItem('Vmc', JSON.stringify(data));
    }

    const handleFilter = (e: string): void => {
        setVmcs([]);
        setFilterData(e);
        setCurrentPage(0);
    }

    const handleSearch = (e: string): void => {
        setVmcs([]);
        searchData["search"] = e;
        setSearchData(searchData);
        formData === "" ? setFormData(" ") : setFormData("");
        setCurrentPage(0);
    }

    const handleSearchColumn1 = (e: string): void => {
        setVmcs([]);
        searchData["description"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn2 = (e: string): void => {
        setVmcs([]);
        searchData["lastMessageReceivedDateTime"] = e;
        setSearchData(searchData);
        handleChange();
    }

    const handleSearchColumn3 = (e: string): void => {
        setVmcs([]);
        searchData["locationDescription"] = e;
        setSearchData(searchData);
        handleChange();
    }    

    const handleRowClick = (tx) => {
        if (window.matchMedia('(pointer: coarse)').matches) {
            setRowClickCount({ [tx.id]: 1 });
        }
        
        setRowClickCount((prev) => {
            const navContext: NavContext = {
                from: 'lists/vmcs',
                id: null
            };

            resetNavContext(navContext);

            const newCount = (prev[tx.id] || 0) + 1;
            if (newCount === 1) {
                setSelectedId(tx.id);
                return { ...prev, [tx.id]: newCount };
            } else if (newCount === 2) {
                setData(tx);
                navigate("/dashboard/details/vmc");
                setSelectedId(tx.id);
                return { ...prev, [tx.id]: 0 };
            }
            return prev;
        });
    };

    const handleRefreshButton = (): void => {
        setVmcs([]);
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const delay = async (ms: number) => {
        await new Promise(resolve => setTimeout(resolve, ms));
    }

    let timer: NodeJS.Timeout | null = null;

    const submitSearch = () => {
        formData === "" ? setFormData(" ") : setFormData("");
    }

    const handleChange = () => {
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(submitSearch, 2000);
        setCurrentPage(0);
    }

    const handlePaging = async (e: number) => {
        errorMsg.length = 0;

        do {
            await delay(1000);
        } while (isRefreshing);     

        setCurrentPage(e);

        const start = ((e + 1) * 25) - 25;
        const end = ((e + 1) * 25);
        const rangeString = `${start}-${end}`
        setPagingString(rangeString);

        document.querySelector('#refresh')?.classList.remove("d-none");
        setIsRefreshing(true);        

        getVmcs(keycloak.token || '', rangeString, { search: formData, Online: filterData, sites: siteFilter.join(), sitesQuery: siteFilterQuery, languageCode: i18n.language }, searchData)
            .then((result) => {
                setVmcs(result.body);
                let v = dummyVmc;
                // try to reuse property metadata from returned items if available
                v.propertyNames = result?.body[0]?.propertyNames;
                
                if (!v?.propertyNames) {
                    getPropertyInfo(keycloak.token || '').then((result) => {
                        v.propertyNames = result;
                    })
                }
                
                setNewVmc(v);
                var totalCount = result.header.split('/')[1];
                setVmcCount(totalCount)
                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            })
            .catch((error: ApiError) => {
                try {
                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                }
                catch (ex) {
                    errorMsg.push("Error handling response");
                    setErrorMsg(errorMsg);
                }

                document.querySelector('#refresh')?.classList.add("d-none");
                setIsRefreshing(false);
            });
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            return false;
        }
        return true;
    }

    const init = () => {
        document.querySelector('.errorMsg')?.classList.add("d-none");
        handlePaging(currentPage ?? 0);
        initSearchFields();
    };

    const initSearchFields = () => {
        Object.keys(searchData).map(x => {
            const search: HTMLInputElement | null = document.querySelector(`.${x}`) as HTMLInputElement;
            if (search) {
                search.value = searchData[x];
            }
        })
    }

    const onResize = () => {
        var e = document.querySelector(".vertical-scrollable") as HTMLElement;
        var h = window.innerHeight;
        e.style.height = (h - 284) + "px";
        e.style.maxHeight = (h - 280) + "px";
        setTableBodyHeight(h - 284);
    };

    useEffect(() => {
        window.addEventListener("resize", onResize);
        onResize();
        if (vmcs?.length === 0) {
            init();
        }
        clearPendingSelectedId("vmcs");
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (vmcs.length > 0) return;
        init();
    }, [formData, filterData]);


    return (
        <>
            <Row className="">                  
                <Col className="">
                    <Card className="rounded">                        
                        <Card.Body className="">
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="d-none d-md-block">
                                <Col>&nbsp;</Col>
                            </Row>
                            <Row className="">
                                <Col sm="12" className="">    
                                    <h4 className="mb-2 qvend-title">{t("automaten")}</h4>                                    
                                </Col>
                            </Row>
                            <form className="" onKeyDown={handleKeyDown}>
                                <Row style={{ fontSize: '11px' }}>
                                    <Col md={3} xs={5} style={{ paddingRight: 0 }}>
                                        <div className="bd-example">
                                            <div>
                                                <SearchInput className="form-control search" type="search" placeholder={t("search")} onChange={handleSearch} value={searchData.search ?? ""} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3} xs={1}>
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.3rem' }}>...</button>
                                    </Col>                                    
                                    <Col className="text-end">
                                        {hasSites(newVmc) ? (<Link to="/dashboard/details/vmc" className="btn btn-qvend" style={{ fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }} onClick={() => {
                                            let siteIdValue: string = "";
                                            if (newVmc && newVmc!.propertyNames && newVmc!.propertyNames["siteId"] && newVmc!.propertyNames["siteId"]["predefinedValues"]) {
                                                let values = newVmc!.propertyNames["siteId"]["predefinedValues"];
                                                if (values.length === 1) {
                                                    siteIdValue = values[0];
                                                }
                                            }
                                            newVmc && (newVmc.siteId = siteIdValue ? siteIdValue : (siteFilter.length === 1 ? siteFilter.pop()!.toString() : "0")); setData(newVmc!);
                                            const navContext: NavContext = {
                                                from: 'lists/vmcs',
                                                id: null
                                            };
                                            resetNavContext(navContext);
                                        }} key={0}>+</Link>) : (
                                            <a className="btn btn-qvend" style={{ pointerEvents: 'none', color: 'gray', backgroundColor: 'lightgray', cursor: 'default', fontSize: '11px', padding: '0.5rem 0.5rem', margin: '5px' }}
                                            onClick={(e) => e.preventDefault()}
                                        >+</a>
                                        ) }
                                        <button type="button" className="btn btn-qvend" style={{ fontSize: '11px' }}>Download</button>
                                    </Col>
                                </Row>
                                <Row>
                                <Col md={12} xs={10} style={{ paddingRight: 0 }}>
                                    <div>
                                        <fieldset className="form-control-sm" style={{ fontSize: '11px' }}>
                                            <Form.Check className="form-check-inline">
                                                <FormCheck.Input onChange={() =>
                                                        handleFilter("")
                                                } type="radio" name="radios" className="form-check-input" id="radio1" defaultChecked={filterData === ""} />
                                                <FormCheck.Label className="form-check-label" htmlFor="radio1">{t("vmcs.filter.all")}</FormCheck.Label>
                                            </Form.Check>
                                            <Form.Check className="form-check-inline">
                                                <FormCheck.Input onChange={() =>
                                                        handleFilter("true")
                                                } type="radio" name="radios" className="form-check-input" id="radio2" defaultChecked={filterData === "true"}/>
                                                <FormCheck.Label className="form-check-label" htmlFor="radio2">{t("vmcs.filter.online")}</FormCheck.Label>
                                            </Form.Check>
                                            <Form.Check className="form-check-inline">
                                                <FormCheck.Input onChange={() =>
                                                        handleFilter("false")
                                                } type="radio" name="radios" className="form-check-input" id="radio3" defaultChecked={filterData === "false"}/>
                                                <FormCheck.Label className="form-check-label" htmlFor="radio3">{t("vmcs.filter.offline")}</FormCheck.Label>
                                            </Form.Check>
                                                {((siteFilter && siteFilter.length > 0) || (siteFilterQuery && siteFilterQuery.length > 2)) ? <span className="text-end">
                                                    <Badge pill bg="dark" className="p-1 " onClick={() => { setSiteFilter([]); setSiteFilterQuery(""); setVmcs([]); setCurrentPage(0); formData === "" ? setFormData(" ") : setFormData(""); window.localStorage.removeItem("searchDataSites") }}><CloseButton variant="white" /> Sites</Badge></span> : null}
                                        </fieldset>
                                    </div>
                                </Col>
                                </Row>
                            </form>
                            <Row>
                                <Col sm="12" xs="12" className="mt-2">
                                    <RefreshPull isRefreshing={isRefreshing} onSetPage={handlePaging} />
                                    <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                        <div>
                                            <Row className="qvend-table-font" style={{ background: "#2b2e83", color: "#FFFFFF", overflowY: "scroll" }}>
                                                <Col xl={2} lg={2} md={3} sm={6} xs={6} className="qvend-table table-column-first">{t('vmc.vmc')}</Col>
                                                <Col xl={2} lg={2} md={3} sm={0} xs={0} className="qvend-table d-none d-md-block">{t('vmc.LastSeen')}</Col>
                                                <Col xl={8} lg={8} md={6} sm={6} xs={6} className="qvend-table">{t('vmc.Location')}</Col>                                                
                                            </Row>
                                        </div>
                                        <div className="vertical-scrollable" style={{ overscrollBehaviorY: 'contain' }}>
                                            <Row className={("qvend-table-font")} style={{ background: "#cfd5ea", color: "black" }}>
                                                <Col xl={2} lg={2} md={3} sm={6} xs={6} className="qvend-table search-in-row-first"><SearchInput className="form-control description" type="search" onChange={handleSearchColumn1} value={searchData.description ?? ""} /></Col>
                                                <Col xl={2} lg={2} md={3} sm={0} xs={0} className="qvend-table search-in-row-padding d-none d-md-block"><SearchInput className="form-control lastMessageReceivedDateTime" type="search" onChange={handleSearchColumn2} value={searchData.lastMessageReceivedDateTime ?? ""} /></Col>
                                                <Col xl={8} lg={8} md={6} sm={6} xs={6} className="qvend-table search-in-row-padding"><SearchInput className="form-control locationDescription" type="search" onChange={handleSearchColumn3} value={searchData.locationDescription ?? ""} /></Col>
                                            </Row>
                                            <Row className="d-none errorMsg">
                                                <Col>
                                                    {
                                                        errorMsg.map((item, index) => {
                                                            return <Alert key={index} variant="warning" className="alert" role="alert">
                                                                <span>{item}</span>
                                                            </Alert>;
                                                        })
                                                    }
                                                </Col>
                                            </Row>
                                            {vmcs.map((vmc: VmcStatus, index) => (
                                                <Link to="#" onClick={() => handleRowClick(vmc)} key={vmc.id}>
                                                    <VmcStatusItem
                                                        isSelected={selectedId === vmc.id}
                                                        key={vmc.key}                                                        
                                                        vmcStatus={vmc}
                                                        rowColor={selectedId === vmc.id ? "gray" : index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                        />
                                                </Link>
                                            ))}
                                            {
                                                //Array.from({ ...dummyVmc, length: (tableBodyHeight/16.5) - vmcs.length } as VmcStatus[]).map((vmc: VmcStatus, index) => (
                                                //    <VmcStatusItem
                                                //        key={index+vmcs.length }
                                                //        vmcStatus={dummyVmc}
                                                //        rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                //        className=""
                                                //    />
                                                //))
                                            }
                                        </div>                                        
                                    </div>
                                </Col>                              
                            </Row>
                            <ListPagination
                                articlesCount={VmcCount}
                                currentPage={currentPage}
                                onSetPage={handlePaging}
                                onRefresh={handleRefreshButton} />
                        </Card.Body>
                    </Card>
                </Col>                                    
            </Row>
        </>
    )
}

function hasRole(vmc: any, key: string, keycloak: any) {
    if (!(vmc && vmc!.propertyNames)) {
        return false;
    }

    const roleNames = vmc["propertyNames"][key].roleNames;
    let result: boolean = false;
    var returnValue = roleNames === null || keycloak && roleNames.map((role: string) => { result = result || keycloak.hasResourceRole(role) }) && result;
    return returnValue;
}

function hasSites(vmc: any) {
    if (!(vmc && vmc!.propertyNames && vmc!.propertyNames["siteId"] && vmc!.propertyNames["siteId"]["predefinedValues"])) {
        return false;
    }

    const siteIds = Array.from(vmc!.propertyNames["siteId"]["predefinedValues"]);

    if (siteIds.length === 0) {
        return false;
    }

    return true;
}

export default Vmcs
